import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { RootStore } from "functions/redux/store";
import { getGuideOpenTrip, getGuidePrivateTrip } from "functions/redux/listing/action";
import GuideOpenTripCard from "components/TripCard/GuideOpenTripCard";
import GuidePrivateTripCard from "components/TripCard/GuidePrivateTripCard";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import Input from "shared/Input/Input";
import Select from "shared/Select/Select";
import Loader from "react-loader-spinner";
import { useTranslation } from "react-i18next";

export interface PageManageProductsProps {
  className?: string;
}

const PageManageProducts: FC<PageManageProductsProps> = ({ className = "" }) => {
  const { listing, user } = useSelector((state: RootStore) => state);
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<"all" | "open" | "private">("all");
  const [selectedStatus, setSelectedStatus] = useState<"all" | "published" | "draft" | "archived">("all");

  // Authentication & Role Check Guard
  useEffect(() => {
    if (user.credential) {
      if (parseInt(user.credential.isGuide) !== 1) {
        history.replace("/");
      }
    }
  }, [user.credential, history]);

  // Fetch Open & Private trips for Guide
  useEffect(() => {
    if (user.credential && parseInt(user.credential.isGuide) === 1) {
      if (listing.guideOpenTripList == null) {
        dispatch(getGuideOpenTrip());
      }
      if (listing.guidePrivateTripList == null) {
        dispatch(getGuidePrivateTrip());
      }
    }
  }, [dispatch, user.credential, listing.guideOpenTripList, listing.guidePrivateTripList]);

  const openTrips: IProduct[] = listing.guideOpenTripList || [];
  const privateTrips: IProduct[] = listing.guidePrivateTripList || [];

  // Combined trips tagged with type
  const allProducts = [
    ...openTrips.map((p) => ({ ...p, tripType: "open" as const })),
    ...privateTrips.map((p) => ({ ...p, tripType: "private" as const })),
  ];

  // Metrics calculation
  const totalProductsCount = allProducts.length;
  const openTripsCount = openTrips.length;
  const privateTripsCount = privateTrips.length;
  const publishedCount = allProducts.filter((p) => p.state === "published").length;
  const draftCount = allProducts.filter((p) => p.state === "draft").length;
  const archivedCount = allProducts.filter((p) => p.state === "archived").length;

  // Filtered Products
  const filteredProducts = allProducts.filter((product) => {
    // Type Filter
    if (selectedType === "open" && product.tripType !== "open") return false;
    if (selectedType === "private" && product.tripType !== "private") return false;

    // Status Filter
    if (selectedStatus !== "all" && product.state !== selectedStatus) return false;

    // Search Term Filter
    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      const titleMatch = product.title?.toLowerCase().includes(term);
      const countryMatch = product.country?.[0]?.name?.toLowerCase().includes(term);
      const highlightMatch = product.highlight?.toLowerCase().includes(term);
      return titleMatch || countryMatch || highlightMatch;
    }

    return true;
  });

  const isLoading = listing.guideOpenTripList == null || listing.guidePrivateTripList == null;

  return (
    <div className={`nc-PageManageProducts container py-10 lg:py-16 space-y-10 ${className}`}>
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 border-b border-neutral-200 dark:border-neutral-700 pb-8">
        <div>
          <h1 className="text-3xl font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-3">
            <i className="las la-boxes text-primary-6000 text-4xl"></i>
            Manajemen Produk
          </h1>
          <p className="mt-2 text-neutral-500 dark:text-neutral-400 text-sm md:text-base">
            Kelola katalog Open Trip dan Private Trip Anda secara terpusat.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <ButtonPrimary href="/open-trip-add" className="flex items-center space-x-2">
            <i className="las la-plus text-xl"></i>
            <span>Tambah Open Trip</span>
          </ButtonPrimary>
          <ButtonSecondary href="/private-trip-add" className="flex items-center space-x-2">
            <i className="las la-plus-circle text-xl"></i>
            <span>Tambah Private Trip</span>
          </ButtonSecondary>
        </div>
      </div>

      {/* Metrics Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-2xl border border-neutral-100 dark:border-neutral-700 text-center">
          <span className="block text-2xl font-bold text-neutral-900 dark:text-neutral-100">{totalProductsCount}</span>
          <span className="text-xs text-neutral-500 dark:text-neutral-400 font-medium uppercase tracking-wider">Total Produk</span>
        </div>
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-800/40 text-center">
          <span className="block text-2xl font-bold text-blue-600 dark:text-blue-400">{openTripsCount}</span>
          <span className="text-xs text-blue-600/80 dark:text-blue-400/80 font-medium uppercase tracking-wider">Open Trips</span>
        </div>
        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-2xl border border-purple-100 dark:border-purple-800/40 text-center">
          <span className="block text-2xl font-bold text-purple-600 dark:text-purple-400">{privateTripsCount}</span>
          <span className="text-xs text-purple-600/80 dark:text-purple-400/80 font-medium uppercase tracking-wider">Private Trips</span>
        </div>
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-2xl border border-green-100 dark:border-green-800/40 text-center">
          <span className="block text-2xl font-bold text-green-600 dark:text-green-400">{publishedCount}</span>
          <span className="text-xs text-green-600/80 dark:text-green-400/80 font-medium uppercase tracking-wider">Dipublikasi</span>
        </div>
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-2xl border border-amber-100 dark:border-amber-800/40 text-center">
          <span className="block text-2xl font-bold text-amber-600 dark:text-amber-400">{draftCount}</span>
          <span className="text-xs text-amber-600/80 dark:text-amber-400/80 font-medium uppercase tracking-wider">Draf</span>
        </div>
        <div className="p-4 bg-rose-50 dark:bg-rose-900/20 rounded-2xl border border-rose-100 dark:border-rose-800/40 text-center">
          <span className="block text-2xl font-bold text-rose-600 dark:text-rose-400">{archivedCount}</span>
          <span className="text-xs text-rose-600/80 dark:text-rose-400/80 font-medium uppercase tracking-wider">Diarsip</span>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 p-4 bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm">
        {/* Search */}
        <div className="relative flex-1">
          <i className="las la-search absolute left-3 top-1/2 -translate-y-1/2 text-xl text-neutral-400"></i>
          <Input
            type="text"
            placeholder="Cari berdasarkan judul trip, negara, atau destinasi..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-xl"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center space-x-2">
            <span className="text-xs font-semibold text-neutral-500 uppercase">Tipe:</span>
            <Select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as any)}
              className="rounded-xl text-sm"
            >
              <option value="all">Semua Tipe</option>
              <option value="open">Open Trip</option>
              <option value="private">Private Trip</option>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-xs font-semibold text-neutral-500 uppercase">Status:</span>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as any)}
              className="rounded-xl text-sm"
            >
              <option value="all">Semua Status</option>
              <option value="published">Dipublikasi (Published)</option>
              <option value="draft">Draf (Draft)</option>
              <option value="archived">Diarsip (Archived)</option>
            </Select>
          </div>
        </div>
      </div>

      {/* Content / Product List Section */}
      {isLoading ? (
        <div className="py-20 flex flex-col items-center justify-center space-y-4">
          <Loader type="Circles" color="#d24e29" height={40} width={40} />
          <span className="text-neutral-500 dark:text-neutral-400 text-sm">Memuat data produk...</span>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="py-16 text-center border-2 border-dashed border-neutral-200 dark:border-neutral-800 rounded-3xl p-8">
          <i className="las la-box-open text-6xl text-neutral-300 dark:text-neutral-600 mb-3 block"></i>
          <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-100">Tidak ada produk ditemukan</h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 max-w-md mx-auto">
            {searchTerm || selectedType !== "all" || selectedStatus !== "all"
              ? "Coba ubah kata kunci pencarian atau kriteria filter yang Anda pilih."
              : "Anda belum menambahkan produk Open Trip atau Private Trip."}
          </p>
          {(searchTerm || selectedType !== "all" || selectedStatus !== "all") && (
            <ButtonSecondary
              className="mt-4"
              onClick={() => {
                setSearchTerm("");
                setSelectedType("all");
                setSelectedStatus("all");
              }}
            >
              Reset Filter
            </ButtonSecondary>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {filteredProducts.map((product) =>
            product.tripType === "open" ? (
              <GuideOpenTripCard key={`open-${product.id}`} data={product} />
            ) : (
              <GuidePrivateTripCard key={`private-${product.id}`} data={product} user={user} />
            )
          )}
        </div>
      )}
    </div>
  );
};

export default PageManageProducts;
